/**
 * Builders for the analytics payloads dispatched from the CoverageDetails
 * page. Centralizing them ensures every event keeps the same `screen_name`,
 * `patient_name`, and `payer_name` boilerplate that downstream BigQuery
 * dashboards rely on.
 */
import { Demographics } from "../../../../data-model/medicalPaOrdersModel";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";

interface CoverageAnalyticsContext {
  demographics: Demographics | undefined;
  orderId: string;
  orgId: string;
  userId: string;
  userEmail: string;
  payerName: string;
}

/**
 * Returns the patient identifier and full name fragments used by every
 * coverage event. `patient_name` keeps the original concatenation behavior
 * (with `undefined` segments left as-is) so payload shapes match the
 * pre-refactor calls byte-for-byte.
 */
const buildBaseFields = (ctx: CoverageAnalyticsContext) => ({
  patient_id: ctx.demographics?.patient_id ?? "",
  order_id: ctx.orderId,
  org_id: ctx.orgId,
  user_id: ctx.userId,
  user_email: ctx.userEmail,
});

const buildConcatPatientName = (demographics: Demographics | undefined) =>
  demographics?.first_name + " " + demographics?.last_name;

const buildSafePatientName = (demographics: Demographics | undefined) =>
  (demographics?.first_name ?? "") + " " + (demographics?.last_name ?? "");

/**
 * Continue button click on the coverage screen footer (active insurance path).
 */
export const buildContinueButtonEvent = (ctx: CoverageAnalyticsContext) => ({
  ...buildBaseFields(ctx),
  event_name: EventsName.CONTINUE_BUTTON_CLICKED,
  additional_data: {
    screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
    patient_name: buildConcatPatientName(ctx.demographics),
    patient_mrn: ctx.demographics?.patient_id ?? "",
    payer_name: ctx.payerName,
  },
});

/**
 * Raise-query modal opened (inactive primary insurance path).
 */
export const buildRaiseQueryModalOpenedEvent = (
  ctx: CoverageAnalyticsContext,
) => ({
  ...buildBaseFields(ctx),
  event_name: EventsName.RAISE_QUERY_MODAL_OPENED,
  additional_data: {
    screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
    patient_name: buildConcatPatientName(ctx.demographics),
    source: ModalNamesForBigQuery.RAISE_QUERY_MODAL,
    patient_mrn: ctx.demographics?.patient_id ?? "",
    payer_name: ctx.payerName,
  },
});

/**
 * Report Inaccuracy header click. Uses the safe (?? "") name fragments and
 * falls back from `patient_id` to `patient_mrn` to mirror the original code.
 */
export const buildReportInaccuracyEvent = (ctx: CoverageAnalyticsContext) => ({
  ...buildBaseFields(ctx),
  patient_id:
    ctx.demographics?.patient_id ?? ctx.demographics?.patient_mrn ?? "",
  event_name: EventsName.REPORT_INACCURACY_CLICKED,
  additional_data: {
    screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
    patient_name: buildSafePatientName(ctx.demographics),
    payer_name: ctx.payerName,
  },
});

/**
 * Validate header click event.
 */
export const buildValidateEvClickedEvent = (ctx: CoverageAnalyticsContext) => ({
  ...buildBaseFields(ctx),
  event_name: EventsName.VALIDATE_EV_CLICKED,
  additional_data: {
    screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
    patient_name: buildConcatPatientName(ctx.demographics),
    payer_name: ctx.payerName,
  },
});

/**
 * Refresh button click on the coverage screen header.
 */
export const buildRefreshButtonEvent = (ctx: CoverageAnalyticsContext) => ({
  ...buildBaseFields(ctx),
  event_name: EventsName.REFRESH_BUTTON_CLICKED + " (Coverage Screen)",
  additional_data: {
    screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
    patient_name: buildConcatPatientName(ctx.demographics),
    payer_name: ctx.payerName,
  },
});

/**
 * Footer Go Back button click event.
 */
export const buildGoBackEvent = (ctx: CoverageAnalyticsContext) => ({
  ...buildBaseFields(ctx),
  event_name: EventsName.GO_BACK_BUTTON_CLICKED,
  additional_data: {
    screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
    source: ScreenNamesForBigQuery.COVERAGE_SCREEN,
    payer_name: ctx.payerName,
  },
});

export type { CoverageAnalyticsContext };
